// src/pages/Sales/SalesOverviewPage.jsx
//
// Sales Overview Page
// =============================================================================
//
// PURPOSE
// -------
// Provides the operational overview screen for reception sales documents.
//
// This file intentionally uses the new SalesOverviewPage name. The route remains
// `/sales`, but the file/component name now describes the real screen purpose:
// this is the operational overview, while SalesDocumentEditPage owns create/edit.
//
// The visible user-facing concept follows the catalog/voucher pattern used
// elsewhere in hb_visits:
//
//   Sales Overview
//     - browse existing sales documents,
//     - filter by status/type/search text,
//     - open an existing document,
//     - create a new sales document,
//     - post or cancel draft documents through backend actions.
//
//   SalesDocumentEditPage
//     - create/edit the actual document details,
//     - maintain buyer/payment/header data,
//     - maintain product/service/manual lines,
//     - save draft and post.
//
// WHY THIS PAGE DOES NOT POST STOCK ITSELF
// ----------------------------------------
// Product sale lines reduce stock only when the backend posts the sales
// document. This overview intentionally delegates all state transitions to the
// backend:
//
//   POST /sales/documents/:id/post
//   POST /sales/documents/:id/cancel
//
// The frontend only asks for the transition and reloads the list afterwards.
// This keeps stock valuation, movement creation, document numbering and status
// validation in one authoritative backend transaction.
//
// DESIGN DIRECTION
// ----------------
// The visual design follows the current ProductOverviewPage direction:
// - compact toolbar,
// - lightweight summary cards,
// - horizontal filter panel,
// - table scroller with sticky header,
// - shared PaginationFooter as a normal flex child,
// - no fixed footer and no global CSS bleed.
//
// I18N NOTE
// ---------
// All user-facing labels, helper texts, empty states and button captions are
// translated through i18next. Backend values such as `draft`, `posted`,
// `cancelled`, `receipt`, `invoice` and `internal_sale` remain stable technical
// values and are translated only at the UI edge.

import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import PendingStockOperationNotice from "../../components/stock/PendingStockOperationNotice";
import { stockPostingErrorMessage } from "../../utils/stockPostingPresentation";

import {
  fetchSalesDocuments,
  postSalesDocument,
  cancelSalesDocument,
  downloadSalesDocumentPdf,
} from "../../services/salesService";
import useClientPagination from "../../hooks/useClientPagination";
import PaginationFooter from "../../components/common/PaginationFooter";

import "./SalesOverviewPage.css";

const PAGE_SIZE_STORAGE_KEY = "sales_overview_page_size";

function safeString(value) {
  return String(value ?? "").trim();
}

function readStoredPageSize(fallback = 25) {
  try {
    const n = Number(localStorage.getItem(PAGE_SIZE_STORAGE_KEY));
    return Number.isInteger(n) && n > 0 ? n : fallback;
  } catch {
    return fallback;
  }
}

function writeStoredPageSize(value) {
  try {
    localStorage.setItem(PAGE_SIZE_STORAGE_KEY, String(value));
  } catch {
    // Ignore storage errors. Page-size persistence is a convenience only and
    // must never block the operational sales overview.
  }
}

function normalizeStatus(status) {
  return safeString(status || "draft").toLowerCase();
}

function normalizeDocumentType(type) {
  return safeString(type || "receipt").toLowerCase();
}

function formatMoney(value, currency = "PLN") {
  const n = Number(value || 0);
  return `${n.toFixed(2)} ${currency || "PLN"}`;
}

function formatDate(value) {
  if (!value) return "—";
  return String(value).slice(0, 10);
}

function statusLabel(t, status) {
  const value = normalizeStatus(status);
  return t(`sales:statuses.${value}`, { defaultValue: value });
}

function documentTypeLabel(t, type) {
  const value = normalizeDocumentType(type);
  return t(`sales:documentTypes.${value}`, { defaultValue: value });
}

function statusClassName(status) {
  const value = normalizeStatus(status).replace(/[^a-z0-9_-]/gi, "");
  return `sales-overview-status sales-overview-status--${value || "draft"}`;
}

function lineCount(doc) {
  return Array.isArray(doc?.lines) ? doc.lines.length : 0;
}

function moneyValue(doc, fieldName) {
  // Backend documents expose totals as decimal-like values. The overview treats
  // missing values as zero so old/incomplete draft records do not break the UI.
  const value = Number(doc?.[fieldName] || 0);
  return Number.isFinite(value) ? value : 0;
}

function documentDisplayNumber(t, doc) {
  return doc?.document_number || t("sales:editor.draftNumber", { id: doc?.id });
}

function buyerDisplayName(t, doc) {
  return safeString(doc?.buyer_name) || t("sales:buyerTypes.anonymous");
}

function salesOverviewErrorMessage(err, t) {
  // Keep the overview user-facing. Backend/service errors may carry endpoint
  // prefixes for debugging, but known business errors should be translated and
  // explained in operator language. Raw technical texts such as
  // "downloadSalesDocumentPdf: Request failed with status code 500" are useful
  // in console/debug logs, but not in the operational overview.
  const code = safeString(err?.code || err?.data?.code).toUpperCase();
  const message = safeString(err?.message);
  const userMessageKey = safeString(err?.userMessageKey);

  if (userMessageKey) {
    return t(userMessageKey);
  }

  if (code === "INSUFFICIENT_STOCK" || message.includes("Insufficient stock")) {
    return t("sales:messages.insufficientStockSelectedLocation");
  }

  if (
    code === "INVOICE_PDF_DOWNLOAD_FAILED" ||
    code === "INVOICE_PDF_ERROR" ||
    message.includes("downloadSalesDocumentPdf") ||
    message.includes("Request failed with status code 500")
  ) {
    return t("sales:messages.pdfOpenFailed");
  }

  return message || t("sales:messages.operationFailed");
}

function openBlobInNewTab(blob) {
  const url = URL.createObjectURL(blob);
  const win = window.open(url, "_blank", "noopener,noreferrer");

  // If a popup blocker prevents opening the new tab, still revoke the object URL
  // later. The caller also has a fallback download path through the browser.
  window.setTimeout(() => URL.revokeObjectURL(url), win ? 60_000 : 5_000);
}

/**
 * SalesOverviewPage.
 *
 * The component is an overview, not the document editor. It fetches sales
 * documents using the existing sales service and presents them in a compact
 * management/reception table. Posting and cancellation remain backend-owned
 * actions. This follows the same cross-layer rule as the stock module: the UI
 * must not update stock or document state directly.
 */
const SalesOverviewPage = () => {
  const navigate = useNavigate();
  const { t } = useTranslation(["common", "sales", "stock"]);

  const [documents, setDocuments] = useState([]);
  const [filters, setFilters] = useState({
    status: "",
    document_type: "",
    q: "",
  });
  const [loading, setLoading] = useState(false);
  const [workingId, setWorkingId] = useState(null);
  const [error, setError] = useState("");
  const [info, setInfo] = useState("");

  /**
   * Backend filter contract.
   *
   * The sales endpoint already accepts status/type/search filters. Empty values
   * are omitted, so the backend keeps its normal defaults. The overview does
   * not invent a second domain model; it only builds a clean query object for
   * the service layer.
   */
  const activeFilters = useMemo(() => {
    const params = {};
    if (filters.status) params.status = filters.status;
    if (filters.document_type) params.document_type = filters.document_type;
    if (filters.q.trim()) params.q = filters.q.trim();
    return params;
  }, [filters]);

  const loadDocuments = useCallback(async () => {
    setLoading(true);
    setError("");

    try {
      const rows = await fetchSalesDocuments(activeFilters);
      setDocuments(Array.isArray(rows) ? rows : []);
    } catch (err) {
      setError(salesOverviewErrorMessage(err, t) || t("sales:messages.loadDocumentsFailed"));
    } finally {
      setLoading(false);
    }
  }, [activeFilters, t]);

  useEffect(() => {
    loadDocuments();
  }, [loadDocuments]);

  /**
   * Summary cards are intentionally derived from the currently loaded list.
   *
   * This means they describe exactly what the operator sees after the backend
   * filters were applied. They do not try to become a financial report. For
   * management-grade revenue reporting, the dedicated Reports module remains
   * the source of interpretation.
   */
  const summary = useMemo(() => {
    const safeDocs = Array.isArray(documents) ? documents : [];
    const draftCount = safeDocs.filter((doc) => normalizeStatus(doc.status) === "draft").length;
    const postedDocs = safeDocs.filter((doc) => normalizeStatus(doc.status) === "posted");
    const cancelledCount = safeDocs.filter((doc) => normalizeStatus(doc.status) === "cancelled").length;
    const totalLineCount = safeDocs.reduce((sum, doc) => sum + lineCount(doc), 0);

    // These totals are derived from the currently loaded backend result set.
    // Because the backend endpoint is already filter-aware, the numbers describe
    // the same filtered document population that the operator is currently
    // paging through. They are an operational overview, not an accounting report.
    const netTotal = safeDocs.reduce((sum, doc) => sum + moneyValue(doc, "net_total"), 0);
    const vatTotal = safeDocs.reduce((sum, doc) => sum + moneyValue(doc, "vat_total"), 0);
    const grossTotal = safeDocs.reduce((sum, doc) => sum + moneyValue(doc, "gross_total"), 0);

    return {
      totalDocuments: safeDocs.length,
      draftCount,
      postedCount: postedDocs.length,
      cancelledCount,
      netTotal,
      vatTotal,
      grossTotal,
      totalLineCount,
    };
  }, [documents]);

  const pagination = useClientPagination(documents, {
    initialPageSize: readStoredPageSize(25),
  });

  const {
    page,
    pageSize,
    pageCount,
    totalRows,
    paginatedItems,
    setPageSize,
    goPrev,
    goNext,
    canPrev,
    canNext,
  } = pagination;

  const handlePageSizeChange = useCallback(
    (nextPageSize) => {
      writeStoredPageSize(nextPageSize);
      setPageSize(nextPageSize);
    },
    [setPageSize]
  );

  function updateFilter(name, value) {
    setFilters((prev) => ({ ...prev, [name]: value }));
  }

  function clearFilters() {
    setFilters({ status: "", document_type: "", q: "" });
  }

  // Post and cancel are intentionally thin UI actions. The backend performs all
  // state transition validation, number allocation and stock side effects.
  async function handlePost(id) {
    setWorkingId(id);
    setError("");
    setInfo("");

    try {
      await postSalesDocument(id);
      setInfo(t("sales:messages.postedFromList"));
      await loadDocuments();
    } catch (err) {
      setError(stockPostingErrorMessage(err, t));
    } finally {
      setWorkingId(null);
    }
  }

  async function handleCancel(id) {
    setWorkingId(id);
    setError("");
    setInfo("");

    try {
      await cancelSalesDocument(id);
      setInfo(t("sales:messages.cancelledFromList"));
      await loadDocuments();
    } catch (err) {
      setError(salesOverviewErrorMessage(err, t) || t("sales:messages.cancelFailed"));
    } finally {
      setWorkingId(null);
    }
  }

  async function handleOpenPdf(id) {
    setWorkingId(id);
    setError("");
    setInfo("");

    try {
      const result = await downloadSalesDocumentPdf(id);
      if (result?.blob) {
        openBlobInNewTab(result.blob);
      }
    } catch (err) {
      setError(salesOverviewErrorMessage(err, t) || t("sales:messages.pdfOpenFailed"));
    } finally {
      setWorkingId(null);
    }
  }

  const paginationSummary = (
    <div className="sales-overview-pagination-summary">
      <span className="sales-overview-pagination-summary-item">
        <span>{t("sales:overview.pagination.documents")}</span>
        <strong>{totalRows}</strong>
      </span>
      <span className="sales-overview-pagination-summary-item">
        <span>{t("sales:overview.pagination.net")}</span>
        <strong>{formatMoney(summary.netTotal, "PLN")}</strong>
      </span>
      <span className="sales-overview-pagination-summary-item">
        <span>{t("sales:overview.pagination.vat")}</span>
        <strong>{formatMoney(summary.vatTotal, "PLN")}</strong>
      </span>
      <span className="sales-overview-pagination-summary-item">
        <span>{t("sales:overview.pagination.gross")}</span>
        <strong>{formatMoney(summary.grossTotal, "PLN")}</strong>
      </span>
    </div>
  );

  return (
    <div className="sales-overview">
      <PendingStockOperationNotice scope="sales" onResolved={loadDocuments} />
      <div className="sales-overview-toolbar">
        <div className="sales-overview-title-block">
          <p className="sales-overview-eyebrow">{t("sales:overview.eyebrow")}</p>
          <h1>{t("sales:overview.title")}</h1>
          <p className="sales-overview-subtitle">{t("sales:overview.subtitle")}</p>
        </div>

        <div className="sales-overview-toolbar-actions">
          <button
            type="button"
            className="sales-overview-btn sales-overview-btn--secondary"
            onClick={loadDocuments}
            disabled={loading}
          >
            {t("common:actions.refresh")}
          </button>

          <button
            type="button"
            className="sales-overview-btn sales-overview-btn--primary"
            onClick={() => navigate("/sales/new")}
          >
            {t("sales:overview.newDocument")}
          </button>
        </div>
      </div>

      <section className="sales-overview-summary" aria-label={t("sales:overview.summary.ariaLabel")}>
        <div className="sales-overview-card">
          <span>{t("sales:overview.summary.loadedDocuments")}</span>
          <strong>{summary.totalDocuments}</strong>
          <small>{t("sales:overview.summary.totalLines", { count: summary.totalLineCount })}</small>
        </div>

        <div className="sales-overview-card sales-overview-card--draft">
          <span>{t("sales:statuses.draft")}</span>
          <strong>{summary.draftCount}</strong>
          <small>{t("sales:overview.summary.draftHint")}</small>
        </div>

        <div className="sales-overview-card sales-overview-card--posted">
          <span>{t("sales:statuses.posted")}</span>
          <strong>{summary.postedCount}</strong>
          <small>{formatMoney(summary.grossTotal, "PLN")}</small>
        </div>

        <div className="sales-overview-card sales-overview-card--cancelled">
          <span>{t("sales:statuses.cancelled")}</span>
          <strong>{summary.cancelledCount}</strong>
          <small>{t("sales:overview.summary.cancelledHint")}</small>
        </div>
      </section>

      <section className="sales-overview-filter-panel" aria-label={t("sales:overview.filters.ariaLabel")}>
        <label className="sales-overview-field">
          <span>{t("sales:overview.filters.status")}</span>
          <select
            value={filters.status}
            onChange={(e) => updateFilter("status", e.target.value)}
          >
            <option value="">{t("sales:overview.filters.allStatuses")}</option>
            <option value="draft">{t("sales:statuses.draft")}</option>
            <option value="posted">{t("sales:statuses.posted")}</option>
            <option value="cancelled">{t("sales:statuses.cancelled")}</option>
          </select>
        </label>

        <label className="sales-overview-field">
          <span>{t("sales:overview.filters.type")}</span>
          <select
            value={filters.document_type}
            onChange={(e) => updateFilter("document_type", e.target.value)}
          >
            <option value="">{t("sales:overview.filters.allTypes")}</option>
            <option value="receipt">{t("sales:documentTypes.receipt")}</option>
            <option value="invoice">{t("sales:documentTypes.invoice")}</option>
            <option value="internal_sale">{t("sales:documentTypes.internal_sale")}</option>
          </select>
        </label>

        <label className="sales-overview-field sales-overview-field--search">
          <span>{t("sales:overview.filters.search")}</span>
          <input
            value={filters.q}
            onChange={(e) => updateFilter("q", e.target.value)}
            placeholder={t("sales:overview.filters.searchPlaceholder")}
          />
        </label>

        <div className="sales-overview-filter-actions">
          <button
            type="button"
            className="sales-overview-btn sales-overview-btn--ghost"
            onClick={clearFilters}
            disabled={!filters.status && !filters.document_type && !filters.q}
          >
            {t("common:actions.clear")}
          </button>
        </div>
      </section>

      {error && <div className="sales-overview-alert sales-overview-alert--danger">{error}</div>}
      {info && <div className="sales-overview-alert sales-overview-alert--success">{info}</div>}

      <section className="sales-overview-table-wrap" aria-label={t("sales:overview.table.ariaLabel")}>
        <div className="sales-overview-table-scroller">
          <table className="sales-overview-table">
            <thead>
              <tr>
                <th>{t("sales:overview.columns.number")}</th>
                <th>{t("sales:overview.columns.date")}</th>
                <th>{t("sales:overview.columns.type")}</th>
                <th>{t("sales:overview.columns.status")}</th>
                <th>{t("sales:overview.columns.buyer")}</th>
                <th className="sales-overview-cell--numeric">{t("sales:overview.columns.netTotal")}</th>
                <th className="sales-overview-cell--numeric">{t("sales:overview.columns.vatTotal")}</th>
                <th className="sales-overview-cell--numeric">{t("sales:overview.columns.grossTotal")}</th>
                <th className="sales-overview-cell--numeric">{t("sales:overview.columns.lines")}</th>
                <th className="sales-overview-cell--actions">{t("sales:overview.columns.actions")}</th>
              </tr>
            </thead>

            <tbody>
              {loading && (
                <tr>
                  <td colSpan="10" className="sales-overview-empty-cell">
                    {t("sales:overview.loading")}
                  </td>
                </tr>
              )}

              {!loading && paginatedItems.length === 0 && (
                <tr>
                  <td colSpan="10" className="sales-overview-empty-cell">
                    <div className="sales-overview-empty-state">
                      <strong>{t("sales:overview.empty.title")}</strong>
                      <span>{t("sales:overview.empty.subtitle")}</span>
                      <button
                        type="button"
                        className="sales-overview-btn sales-overview-btn--primary"
                        onClick={() => navigate("/sales/new")}
                      >
                        {t("sales:overview.newDocument")}
                      </button>
                    </div>
                  </td>
                </tr>
              )}

              {!loading && paginatedItems.map((doc) => {
                const isDraft = normalizeStatus(doc.status) === "draft";
                const disabled = workingId === doc.id;

                return (
                  <tr key={doc.id}>
                    <td className="sales-overview-cell--strong">
                      <Link to={`/sales/${doc.id}`} className="sales-overview-doc-link">
                        {documentDisplayNumber(t, doc)}
                      </Link>
                    </td>
                    <td>{formatDate(doc.sale_date)}</td>
                    <td>{documentTypeLabel(t, doc.document_type)}</td>
                    <td>
                      <span className={statusClassName(doc.status)}>
                        {statusLabel(t, doc.status)}
                      </span>
                    </td>
                    <td>{buyerDisplayName(t, doc)}</td>
                    <td className="sales-overview-cell--numeric">
                      {formatMoney(doc.net_total, doc.currency)}
                    </td>
                    <td className="sales-overview-cell--numeric">
                      {formatMoney(doc.vat_total, doc.currency)}
                    </td>
                    <td className="sales-overview-cell--numeric">
                      {formatMoney(doc.gross_total, doc.currency)}
                    </td>
                    <td className="sales-overview-cell--numeric">{lineCount(doc)}</td>
                    <td className="sales-overview-cell--actions">
                      <div className="sales-overview-row-actions">
                        <Link className="sales-overview-btn sales-overview-btn--tiny" to={`/sales/${doc.id}`}>
                          {t("common:actions.open")}
                        </Link>

                        {!isDraft && normalizeStatus(doc.status) === "posted" && (
                          <button
                            type="button"
                            className="sales-overview-btn sales-overview-btn--tiny sales-overview-btn--pdf"
                            onClick={() => handleOpenPdf(doc.id)}
                            disabled={disabled}
                            title={t("sales:overview.actions.openPdfHint")}
                          >
                            {t("sales:overview.actions.openPdf")}
                          </button>
                        )}

                        {isDraft && (
                          <button
                            type="button"
                            className="sales-overview-btn sales-overview-btn--tiny sales-overview-btn--post"
                            onClick={() => handlePost(doc.id)}
                            disabled={disabled}
                          >
                            {t("common:actions.post")}
                          </button>
                        )}

                        {isDraft && (
                          <button
                            type="button"
                            className="sales-overview-btn sales-overview-btn--tiny sales-overview-btn--danger"
                            onClick={() => handleCancel(doc.id)}
                            disabled={disabled}
                          >
                            {t("common:actions.cancel")}
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>

        <PaginationFooter
          className="sales-overview-pagination"
          page={page}
          pageCount={pageCount}
          pageSize={pageSize}
          pageSizeOptions={[10, 25, 50, 100]}
          totalRows={totalRows}
          disabled={loading}
          onPageSizeChange={handlePageSizeChange}
          onPrev={goPrev}
          onNext={goNext}
          canPrev={canPrev}
          canNext={canNext}
          summary={paginationSummary}
        />
      </section>
    </div>
  );
};

export default SalesOverviewPage;



